<template>
  <div class="container-fluid basic-background">
    <h1 class="visually-hidden">{{ $t("imageChecker") }}</h1>
    <div class="row">
      <!-- 左側：圖片預覽 -->
      <div class="col-lg-8 mb-4">
        <div class="block-card px-4 py-4 d-flex flex-column">
          <div class="d-flex justify-content-between align-items-center mb-4">
            <div class="action-buttons-container">
              <div class="action-buttons d-lg-flex d-none">
                <button class="btn block-btn block-btn-hover me-2" @click="openDatasetModal">{{ $t("switchDataset") }}</button>
                <button class="btn block-btn block-btn-hover me-2" @click="openDirectoryModal">{{ $t("openImageDirectory") }}</button>
              </div>
              <div class="d-lg-none">
                <button class="btn block-btn action-toggle-btn" @click="toggleActionButtons" :class="{ 'active': showActionButtons }" :aria-label="$t('openImageDirectory')">
                  <i :class="['bi', showActionButtons ? 'bi-folder2' : 'bi-folder']"></i>
                </button>
                <div v-show="showActionButtons" class="collapsible-action-buttons">
                  <button class="btn block-btn-white borderless" @click="openDatasetModal">{{ $t("switchDataset") }}</button>
                  <button class="btn block-btn-white borderless" @click="openDirectoryModal">{{ $t("openImageDirectory") }}</button>
                </div>
              </div>
            </div>
            <div class="edit-info mt-3">
              <p class="text-muted">{{ $t("lastEdited") }} {{ selectedImage?.last_editor_name || '—' }}
                <span v-if="selectedImage?.last_edited_at">（{{ formatDate(selectedImage.last_edited_at) }}）</span>
              </p>
            </div>
          </div>
          
          <div class="image-preview-container">
            <div v-if="isImageLoading" class="loading-container">
              <div class="loading-spinner"><div class="gradient-circle"></div><div class="loading-text">載入中...</div></div>
            </div>
            <div v-else-if="selectedImage && selectedImage.image" class="image-display-wrapper">
              <transition name="fade" mode="out-in">
                <img ref="zoomImg" :key="selectedImage.image" :src="selectedImage.image" class="preview-image" :class="{ 'opacity-25': isImageLoading, zoomed: isZoomed }" alt="Preview" @load="onImageLoaded" @error="onImageError" @click="toggleZoom" draggable="false" @contextmenu.prevent>
              </transition>
            </div>
            <div v-else class="no-image-placeholder">
              <div class="placeholder-image-container"><img src="/images/logo.png" alt="預設圖片" class="placeholder-image"></div>
            </div>
          </div>
          
          <div class="d-flex justify-content-center align-items-center my-3">
            <strong class="image-counter me-3">{{ $t("image") }} {{ displayIndex }} / {{ totalImages }}</strong>
            <button class="btn block-btn-white me-2" :disabled="currentImageIndex === 0" @click="prevImage">{{ $t("previous") }}</button>
            <button class="btn block-btn-white" :disabled="currentImageIndex === totalImages - 1" @click="nextImage">{{ $t("next") }}</button>
          </div>
        </div>
      </div>

      <!-- 右側：表單 -->
      <div class="col-lg-4 mb-4">
        <div class="block-card py-4 d-flex flex-column">
          <div class="form-container">
            <!-- 年代 -->
            <div class="form-section">
              <h6 class="section-title">{{ $t("era") }}</h6>
              <div class="ai-result"><span class="ai-label">{{ $t("aiAutoTagging") }}：</span><span class="ai-value">{{ autoYearText() }}</span></div>
              <h6 class="form-label mb-2">{{ $t("ad") }}</h6>
              <div class="year-input-group">
                <label class="visually-hidden" for="year-start">{{ $t("yearStart") }}</label>
                <input
                  id="year-start"
                  type="number"
                  class="form-control year-input"
                  v-model.number="form.user_year_start"
                  :placeholder="$t('yearStart')"
                  min="1800"
                  max="2100"
                  :aria-invalid="!!yearError"
                >

                <span class="year-separator">—</span>

                <label class="visually-hidden" for="year-end">{{ $t("yearEnd") }}</label>
                <input
                  id="year-end"
                  type="number"
                  class="form-control year-input"
                  v-model.number="form.user_year_end"
                  :placeholder="$t('yearEnd')"
                  min="1800"
                  max="2100"
                  :aria-invalid="!!yearError"
                >
              </div>
              <div v-if="yearError" class="error-message">{{ yearError }}</div>
            </div>

            <!-- 地區 -->
            <div class="form-section">
              <h6 class="section-title">{{ $t("region") }}</h6>
              <div class="ai-result"><span class="ai-label">{{ $t("aiAutoTagging") }}：</span><span class="ai-value">{{ selectedImage?.auto_region || '—' }}</span></div>
              <div class="region-input-group">
                <div class="region-field">
                  <label class="form-label" for="region-city">{{ $t("city") }}</label>
                  <select id="region-city" class="form-select" v-model="form.user_city_code" @change="onCityChange" required>
                    <option value="">{{ $t("pleaseSelect") }}</option>
                    <option v-for="c in cities" :key="c.code" :value="c.code">{{ c.name }}</option>
                  </select>
                </div>
                <div class="region-field">
                  <label class="form-label" for="region-township">{{ $t("township") }}</label>
                  <select id="region-township" class="form-select" v-model="form.user_district_code" :disabled="!form.user_city_code" required>
                    <option value="">{{ $t("pleaseSelect") }}</option>
                    <option v-for="d in districts" :key="d.value" :value="d.value">{{ d.label }}</option>
                  </select>
                </div>
                <div class="region-field notes-field">
                  <label class="form-label" for="region-notes">{{ $t("notes") }}</label>
                  <input id="region-notes" class="form-control" v-model="form.user_poi" :placeholder="$t('poiExample')">
                </div>
              </div>
            </div>

            <!-- 四大分類 -->
            <div class="form-section">
              <h6 class="section-title">{{ $t("fourCategories") }}</h6>
              <div class="ai-result"><span class="ai-label">{{ $t("aiAutoTagging") }}：</span><span class="ai-value">{{ selectedImage?.auto_category || '—' }}</span></div>
              <label class="form-label" for="category-select">{{ $t("artificial") }}</label>
              <select id="category-select" class="form-select" v-model="form.user_category" required>
                <option value="">{{ $t("pleaseSelect") }}</option>
                <option value="歷史街景與古蹟建築">{{ $t("cat_1") }}</option>
                <option value="傳統服飾與民俗工藝">{{ $t("cat_2") }}</option>
                <option value="民俗活動與節慶儀式">{{ $t("cat_3") }}</option>
                <option value="自然環境與地方事件">{{ $t("cat_4") }}</option>
              </select>
            </div>

            <!-- 後設資料 -->
            <div class="form-section">
              <h6 class="section-title">{{ $t("metadata") }}</h6>
              <label for="metadata-textarea" class="visually-hidden">{{ $t("metadata") }}</label>
              <textarea id="metadata-textarea" class="form-control metadata-textarea" rows="3" :value="selectedImage?.metadata_text || ''" readonly></textarea>
              <div class="form-help" v-if="!selectedImage?.metadata_text">{{ $t("noMetadata") }}</div>
            </div>

            <!-- Prompt -->
            <div class="form-section">
              <h6 class="section-title">{{ $t("prompt") || 'Prompt' }}</h6>
              <label class="form-label" for="auto-prompt">{{ $t("autoPrompt") }}</label>
              <textarea
                id="auto-prompt"
                class="form-control prompt-textarea"
                rows="3"
                :value="selectedImage?.auto_prompt || ''"
                readonly
              ></textarea>
              <div class="clip-score"><span class="clip-label">Auto CLIP:</span><span class="clip-value">{{ selectedImage?.auto_clip_score ?? '0' }}</span></div>
              <label class="form-label mt-3" for="user-prompt">{{ $t("userPrompt") }}</label>
              <textarea
                id="user-prompt"
                v-model="form.user_prompt"
                class="form-control prompt-textarea"
                rows="3"
                placeholder="Enter corrected prompt..."
              ></textarea>
              <div class="clip-score"><span class="clip-label">User CLIP:</span><span class="clip-value">{{ form?.user_clip_score !== null ? form.user_clip_score : 'Not calculated' }}</span></div>
              <div class="button-row">
                <button class="btn block-btn block-btn-hover calculate-btn" :disabled="isCalculating" @click="recalculateUserClipScore">
                  <span v-if="isCalculating">{{ $t("calculating") }}...</span><span v-else>{{ $t("calculateCLIPScore") }}</span>
                </button>
                <button class="btn block-btn block-btn-hover mark-btn" :disabled="!!validateBeforeSubmit" @click="markImageChecked">{{ $t("markedAndSaved") }}</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Directory Modal -->
    <div id="directoryModal" class="modal fade" tabindex="-1">
      <div class="modal-dialog modal-xl">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ $t("imageDirectory") }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" :aria-label="$t('close')"></button>
          </div>
          <div class="modal-body-filter">
            <div class="filter-row">
              <div class="filter-field">
                <label class="filter-label" for="filter-image-number">{{ $t("imageNumber") }}</label>
                <input id="filter-image-number" v-model="directoryFilters.imageNumber" type="text" class="form-control filter-input" placeholder="例如: 5 或 1-10">
              </div>
              <div class="filter-field">
                <label class="filter-label" for="filter-image-name">{{ $t("imageName") }}</label>
                <input id="filter-image-name" v-model="directoryFilters.imageName" type="text" class="form-control filter-input" placeholder="搜尋圖片名稱...">
              </div>
              <div class="filter-field">
                <label class="filter-label" for="filter-mark">{{ $t("mark") }}</label>
                <select id="filter-mark" v-model="directoryFilters.reviewStatus" class="form-select filter-select">
                  <option value="all">全部</option><option value="reviewed">已校正</option>
                  <option value="unreviewed">未校正</option>
                </select>
              </div>
              <div class="filter-actions"><button v-if="hasDirectoryFilters" class="btn btn-sm block-btn-white" @click="clearDirectoryFilters">清除</button></div>
            </div>
            <div v-if="hasDirectoryFilters" class="filter-stats"><small class="text-muted">顯示 {{ filteredImageDirectory.length }} / {{ imageDirectory.length }} 張圖片</small></div>
          </div>
          <div class="modal-body table-responsive">
            <table class="table table-striped">
              <thead><tr><th scope="col">{{ $t("imageNumber") }}</th><th scope="col">{{ $t("imageName") }}</th><th scope="col">Auto<br>CLIP</th><th scope="col">User<br>CLIP</th><th scope="col" style="width: 5%;">{{ $t("mark") }}</th></tr></thead>
              <tbody>
                <tr v-for="item in paginatedDirectory" :key="item.id" style="cursor: pointer;" @click="selectImageFromList(item)">
                  <td>{{ getActualImageNumber(item) }}</td><td>{{ item.image_name }}</td><td>{{ item.auto_clip_score }}</td><td>{{ item.user_clip_score }}</td>
                  <td><i :class="item.is_reviewed ? 'bi bi-check-lg text-secondary' : 'bi bi-x-lg text-secondary'" /></td>
                </tr>
                <tr v-if="paginatedDirectory.length === 0"><td colspan="5" class="text-center text-muted">{{ hasDirectoryFilters ? '沒有符合條件的圖片' : '無圖片資料' }}</td></tr>
              </tbody>
            </table>
          </div>
          <div v-if="directoryTotalPages > 1" class="modal-footer-pagination">
            <div class="pagination-controls">
              <button class="btn btn-sm block-btn-white pagination-btn" :disabled="directoryCurrentPage === 1" @click="directoryPrevPage" :aria-label="$t('previous')"><i class="bi bi-chevron-left"></i></button>
              <div class="pagination-jump"><span class="pagination-label">跳至</span><label for="directory-page-jump" class="visually-hidden">跳至頁數</label><input id="directory-page-jump" v-model="directoryJumpToPage" type="number" class="form-control pagination-input" min="1" :max="directoryTotalPages" @keypress="handleDirectoryJumpKeyPress"><span class="pagination-label">頁</span></div>
              <div class="pagination-total">/ 共 {{ directoryTotalPages }} 頁</div>
              <button class="btn btn-sm block-btn-white pagination-btn" :disabled="directoryCurrentPage === directoryTotalPages" @click="directoryNextPage" :aria-label="$t('next')"><i class="bi bi-chevron-right"></i></button>
            </div>
            <div class="pagination-current d-lg-none">{{ directoryCurrentPage }} / {{ directoryTotalPages }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Dataset Modal -->
    <div id="datasetModal" class="modal fade" tabindex="-1">
      <div class="modal-dialog modal-xl">
        <div class="modal-content">
          <div class="modal-header"><h5 class="modal-title">{{ $t("selectDataset") }}</h5><button type="button" class="btn-close" data-bs-dismiss="modal" :aria-label="$t('close')"></button></div>
          <div class="modal-body table-responsive">
            <table class="table table-striped">
              <thead><tr><th scope="col">{{ $t("description") }}</th><th scope="col" class="text-truncate">{{ $t("imageFolderPath") }}</th><th scope="col">{{ $t("numsOfImages_1") }}{{ $t("numsOfImages_2") }}</th></tr></thead>
              <tbody><tr v-for="ds in paginatedDatasets" :key="ds.id" style="cursor: pointer;" @click="selectDataset(ds)"><td>{{ ds.description || '—' }}</td><td class="text-truncate" :title="ds.image_dir_path">.../{{ ds.image_dir_path.split('/').slice(-1)[0] }}</td><td>{{ ds.image_nums }}</td></tr></tbody>
            </table>
          </div>
          <div v-if="datasetTotalPages > 1" class="d-flex justify-content-center align-items-center gap-2 mb-3">
            <button class="btn btn-sm block-btn-white" :disabled="datasetCurrentPage === 1" @click="datasetPrevPage">{{ $t("previous") }}</button>
            <span class="pagination-info">{{ datasetCurrentPage }} / {{ datasetTotalPages }}</span>
            <button class="btn btn-sm block-btn-white" :disabled="datasetCurrentPage === datasetTotalPages" @click="datasetNextPage">{{ $t("next") }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Success Modal -->
    <div id="successModal" class="modal fade" tabindex="-1">
      <div class="modal-dialog"><div class="modal-content"><div class="modal-header"><h5 class="modal-title">{{ $t("info") }}</h5><button type="button" class="btn-close" data-bs-dismiss="modal" :aria-label="$t('close')"></button></div><div class="modal-body"><p>{{ successMessage }}</p></div><div class="modal-footer"><button type="button" class="btn btn-secondary" data-bs-dismiss="modal">OK</button></div></div></div>
    </div>
  </div>
</template>

<script>
import api from '@/api/axios';
import * as bootstrap from 'bootstrap'
import config from '@/config.json'
import { City_INDEX } from '@/utils/city-index'

export default {
  name: 'ImageChecker',
  data() {
    return {
      datasets: [], currentDatasetId: null, imageDirectory: [], currentImageIndex: 0,
      currentImage: { id: null, image: '', image_name: '', auto_prompt: '', auto_clip_score: null, user_prompt: '', user_clip_score: null, is_reviewed: false, auto_year_start: '', auto_year_end: '', auto_city_code: '', auto_poi: '', auto_category: '', user_year_start : '', user_year_end : '', user_city_code : '', user_district_code : '', user_poi : '', metadata_text : '', last_editor : '', last_editor_name : '', last_edited_at : '' },
      isCalculating: false, isImageLoading: false, isZoomed: false, successMessage: '',
      form: { user_prompt: "", user_clip_score: null, user_year_start: "", user_year_end: "", user_city_code: "", user_district_code: "", user_category: "", is_reviewed: false },
      cities: [], districts: [], showActionButtons: false,
      directoryCurrentPage: 1, directoryItemsPerPage: 5, datasetCurrentPage: 1, datasetItemsPerPage: 5,
      directoryFilters: { imageNumber: '', imageName: '', reviewStatus: 'all' }, directoryJumpToPage: '1', directoryFilterDebounceTimer: null,
    }
  },
  computed: {
    totalImages() { return this.imageDirectory.length },
    displayIndex() { return this.currentImageIndex + 1 },
    selectedImage() { return this.currentImage },
    yearError() {
      const s = this.form.user_year_start, e = this.form.user_year_end, is4 = (v) => /^\d{4}$/.test(String(v))
      if (s !== '' && !is4(s)) return this.$t('yearError_1')
      if (e !== '' && !is4(e)) return this.$t('yearError_2')
      if (s !== '' && e !== '' && Number(s) > Number(e)) return this.$t('yearError_3')
      return ''
    },
    validateBeforeSubmit() {
      if (this.yearError) return this.yearError
      const c = this.form.user_city_code, z = this.form.user_district_code
      if (c && z && !City_INDEX.helpers.isZipInCity(c, z)) return this.$t('distError')
      return ''
    },
    filteredImageDirectory() {
      let filtered = [...this.imageDirectory]
      if (this.directoryFilters.imageNumber.trim()) {
        const numFilter = this.directoryFilters.imageNumber.trim()
        if (numFilter.includes('-')) {
          const [start, end] = numFilter.split('-').map(n => parseInt(n.trim()))
          filtered = filtered.filter((item, index) => { const num = index + 1; return num >= start && num <= end })
        } else {
          const exactNum = parseInt(numFilter)
          filtered = filtered.filter((item, index) => (index + 1) === exactNum)
        }
      }
      if (this.directoryFilters.imageName.trim()) {
        const nameFilter = this.directoryFilters.imageName.trim().toLowerCase()
        filtered = filtered.filter(item => item.image_name.toLowerCase().includes(nameFilter))
      }
      if (this.directoryFilters.reviewStatus !== 'all') {
        const isReviewed = this.directoryFilters.reviewStatus === 'reviewed'
        filtered = filtered.filter(item => item.is_reviewed === isReviewed)
      }
      return filtered
    },
    paginatedDirectory() {
      const start = (this.directoryCurrentPage - 1) * this.directoryItemsPerPage, end = start + this.directoryItemsPerPage
      return this.filteredImageDirectory.slice(start, end)
    },
    directoryTotalPages() { return Math.ceil(this.filteredImageDirectory.length / this.directoryItemsPerPage) },
    hasDirectoryFilters() { return this.directoryFilters.imageNumber.trim() !== '' || this.directoryFilters.imageName.trim() !== '' || this.directoryFilters.reviewStatus !== 'all' },
    paginatedDatasets() {
      const start = (this.datasetCurrentPage - 1) * this.datasetItemsPerPage, end = start + this.datasetItemsPerPage
      return this.datasets.slice(start, end)
    },
    datasetTotalPages() { return Math.ceil(this.datasets.length / this.datasetItemsPerPage) },
    getActualImageNumber() {
      return (item) => {
        const index = this.imageDirectory.findIndex(i => i.id === item.id)
        return index + 1
      }
    },
  },
  watch: {
    'directoryFilters.imageNumber': function() { this.debouncedDirectoryFilter() },
    'directoryFilters.imageName': function() { this.debouncedDirectoryFilter() },
    'directoryFilters.reviewStatus': function() { this.applyDirectoryFilters() },
    '$i18n.locale'() { this.rebuildCities(); this.rebuildDistricts(); },
    'directoryCurrentPage': function(newPage) {
      this.directoryJumpToPage = String(newPage)
    }
  },
  mounted() { this.rebuildCities(); this.rebuildDistricts(); this.fetchDatasets() },
  beforeUnmount() { this.cleanupDirectoryFilters() },
  methods: {
    async fetchDatasets() {
      try {
        const res = await api.get(`${config.apiBaseUrl}/api/annotations/datasets`)
        this.datasets = res.data

        if (this.datasets.length) {
          this.currentDatasetId = this.datasets[0].id
          await this.fetchImageDirectory()
          this.loadImageDetailByIndex(0)
        }
      } catch (err) {
        console.error(err)
      }
    },
    async fetchImageDirectory() {
      if (!this.currentDatasetId) return
      try {
        const res = await api.get(`${config.apiBaseUrl}/api/annotations/imageDirectoryInfo/${this.currentDatasetId}`)
        this.imageDirectory = res.data
      } catch (err) {
        console.error(err)
      }
    },
    async loadImageDetailByIndex(idx) {
      if (idx < 0 || idx >= this.imageDirectory.length) return
      this.currentImageIndex = idx; this.isImageLoading = true; this.currentImage.image = ''
      try {
        const res = await api.get(`${config.apiBaseUrl}/api/annotations/imageData/${this.imageDirectory[idx].id}`)
        this.currentImage = res.data; this.syncFormFromCurrent();
      } catch (err) { console.error(err) } finally { this.isImageLoading = false }
    },
    syncFormFromCurrent() {
      const img = this.currentImage || {}
      this.form.user_prompt = img.user_prompt || ''; this.form.user_clip_score = img.user_clip_score ?? null
      this.form.is_reviewed = !!img.is_reviewed; this.form.user_year_start = img.user_year_start ?? ''
      this.form.user_year_end = img.user_year_end ?? ''; this.form.user_city_code = img.user_city_code || ''
      this.form.user_district_code = img.user_district_code || ''; this.form.user_poi = img.user_poi || ''
      this.form.user_category = img.user_category || ''; this.rebuildDistricts();
    },
    openDirectoryModal() {
      this.clearDirectoryFilters()
      this.fetchImageDirectory().then(() => { 
        this.directoryJumpToPage = '1'
        new bootstrap.Modal(document.getElementById('directoryModal')).show() 
      })
    },
    openDatasetModal() {
      this.datasetCurrentPage = 1
      new bootstrap.Modal(document.getElementById('datasetModal')).show()
    },
    async selectDataset(ds) {
      this.currentDatasetId = ds.id
      bootstrap.Modal.getInstance(document.getElementById('datasetModal')).hide()
      await this.fetchImageDirectory(); this.loadImageDetailByIndex(0)
    },
    prevImage() { if (this.currentImageIndex > 0) this.loadImageDetailByIndex(this.currentImageIndex - 1) },
    nextImage() { if (this.currentImageIndex < this.totalImages - 1) this.loadImageDetailByIndex(this.currentImageIndex + 1) },
    selectImageFromList(item) {
      const idx = this.imageDirectory.findIndex(i => i.id === item.id)
      if (idx !== -1) this.loadImageDetailByIndex(idx)
      bootstrap.Modal.getInstance(document.getElementById('directoryModal')).hide()
    },
    openSuccessModal(message) { this.successMessage = message; new bootstrap.Modal(document.getElementById('successModal')).show() },
    async markImageChecked() {
      if (!this.selectedImage?.id) return
      try {
        this.selectedImage.is_reviewed = true; this.form.is_reviewed = true
        await api.patch(`${config.apiBaseUrl}/api/annotations/updatePrompt/${this.selectedImage.id}`, this.form)
        this.openSuccessModal(this.$t('markedAndSavedText'))
      } catch (err) { console.error('Failed to save image:', err); this.openSuccessModal('Failed to save image. Please try again.') }
    },
    async recalculateUserClipScore() {
      if (!this.selectedImage?.id || !this.form.user_prompt) return
      this.isCalculating = true
      try {
        const res = await api.post(`${config.apiBaseUrl}/api/annotations/computeClipScore`, { ID: this.selectedImage.id, prompt: this.form.user_prompt })
        if (res.data.clip_score != null) { this.currentImage.user_clip_score = parseFloat(res.data.clip_score.toFixed(2)); this.form.user_clip_score = this.currentImage.user_clip_score }
      } catch (err) { console.error('Recalculate failed:', err); this.openSuccessModal('計算失敗,請稍後再試') } finally { this.isCalculating = false }
    },
    toggleZoom(event) {
      this.isZoomed = !this.isZoomed; const img = this.$refs.zoomImg
      if (!img) return
      const rect = img.getBoundingClientRect(), offsetX = event.clientX - rect.left, offsetY = event.clientY - rect.top
      const originX = (offsetX / rect.width) * 100, originY = (offsetY / rect.height) * 100
      img.style.transform = this.isZoomed ? 'scale(2)' : 'scale(1)'; img.style.transformOrigin = `${originX}% ${originY}%`
    },
    onImageLoaded() {
      this.isZoomed = false; const img = this.$refs.zoomImg
      if (img) { img.style.transform = 'scale(1)'; img.style.transformOrigin = 'center center' }
      this.isImageLoading = false
    },
    onImageError() { 
      this.isImageLoading = false; 
      console.error('Image failed to load:', 
      this.selectedImage?.image) 
    },
    formatDate(dt) {
      if (!dt) {
        return '';
      }

      const d = new Date(dt);
      return d.toLocaleDateString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    },

    onCityChange() { 
      this.rebuildDistricts() 
    },
    currentLang() { 
      const l = (this.$i18n && this.$i18n.locale) ? this.$i18n.locale : 'zh-TW'; 
      return l.startsWith('en') ? 'en' : 'zh' 
    },
    rebuildCities() {
      const lang = this.currentLang()
      this.cities = City_INDEX.cityList.map(c => ({ code: c.code, name: City_INDEX.labels.city(c.code, lang) }))
    },
    rebuildDistricts() {
      const lang = this.currentLang(), code = this.form.user_city_code
      this.districts = City_INDEX.helpers.getZipsByCity(code).map(z => ({ value: z, label: City_INDEX.helpers.getAreaNameByZip(z, lang) }))
      if (this.form.user_district_code && !City_INDEX.helpers.isZipInCity(code, this.form.user_district_code)) 
        this.form.user_district_code = ''
    },
    autoYearText() {
      if (this.selectedImage.auto_year_start && this.selectedImage.auto_year_end) 
        return this.selectedImage.auto_year_start + '-' + this.selectedImage.auto_year_end
      else if(!this.selectedImage.auto_year_start && !this.selectedImage.auto_year_end) 
        return '—'
      else 
        return this.selectedImage.auto_year_start
    },
    toggleActionButtons() { 
      this.showActionButtons = !this.showActionButtons 
    },
    debouncedDirectoryFilter() {
      if (this.directoryFilterDebounceTimer) clearTimeout(this.directoryFilterDebounceTimer)
      this.directoryFilterDebounceTimer = setTimeout(() => { this.applyDirectoryFilters() }, 300)
    },
    applyDirectoryFilters() { 
      this.directoryCurrentPage = 1
      this.directoryJumpToPage = '1'
    },
    clearDirectoryFilters() {
      if (this.directoryFilterDebounceTimer) { clearTimeout(this.directoryFilterDebounceTimer); this.directoryFilterDebounceTimer = null }
      this.directoryFilters = { imageNumber: '', imageName: '', reviewStatus: 'all' }
      this.directoryCurrentPage = 1
      this.directoryJumpToPage = '1'
    },
    directoryJumpTo() {
      const page = parseInt(this.directoryJumpToPage)
      if (isNaN(page) || page < 1 || page > this.directoryTotalPages) { 
        alert(`請輸入 1-${this.directoryTotalPages} 之間的頁碼`)
        this.directoryJumpToPage = String(this.directoryCurrentPage)
        return 
      }
      this.directoryCurrentPage = page
    },
    handleDirectoryJumpKeyPress(event) { 
      if (event.key === 'Enter') this.directoryJumpTo() 
    },
    cleanupDirectoryFilters() { 
      if (this.directoryFilterDebounceTimer) { 
        clearTimeout(this.directoryFilterDebounceTimer); 
        this.directoryFilterDebounceTimer = null 
      } 
    },
    directoryPrevPage() {
      if (this.directoryCurrentPage > 1) 
        this.directoryCurrentPage-- 
    },
    directoryNextPage() {
      if (this.directoryCurrentPage < this.directoryTotalPages) 
        this.directoryCurrentPage++ 
    },
    datasetPrevPage() {
      if (this.datasetCurrentPage > 1) 
        this.datasetCurrentPage-- 
    },
    datasetNextPage() {
      if (this.datasetCurrentPage < this.datasetTotalPages) 
        this.datasetCurrentPage++ 
    }
  }
}
</script>

<style scoped>
.image-preview-container { border-radius: 15px; min-height: 400px; display: flex; align-items: center; justify-content: center; position: relative; overflow: hidden; }
.image-display-wrapper { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; padding: 20px; }
.preview-image { max-width: 100%; max-height: 100%; object-fit: contain; border-radius: 10px; box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1); transition: transform 0.2s ease; user-select: none; cursor: zoom-in; }
.preview-image.zoomed { cursor: zoom-out; }
.no-image-placeholder { text-align: center; }
.form-section { border-bottom: 1px solid #e0e0e0; padding: 20px; }
.form-section:last-child { border: none; }
.section-title { font-size: 1.1rem; font-weight: 600; color: #333; margin-bottom: 15px; padding-bottom: 8px; }
.ai-result { display: inline-block; background: #d9d9d9; border-radius: 10px; padding: 0 10px; margin-bottom: 15px; font-size: 0.9rem; }
.ai-label { font-weight: 500; color: #666; letter-spacing: 1px; }
.ai-value { color: #333; letter-spacing: 1px; }
.year-input-group { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
.year-input { flex: 1; }
.year-input[type="number"] { -moz-appearance: textfield; }
.year-input[type="number"]::-webkit-outer-spin-button, .year-input[type="number"]::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
.year-separator { color: #666; font-weight: 500; }
.region-input-group { display: grid; grid-template-columns: 1fr 1fr; grid-template-rows: auto auto; gap: 12px; }
.region-field.notes-field { grid-column: 1 / -1; }
.region-field { display: flex; flex-direction: column; }
.prompt-textarea { resize: vertical; min-height: 80px; }
.metadata-textarea { resize: vertical; background-color: #f8f9fa; }
.clip-score { display: inline-block; background: #d9d9d9; border-radius: 10px; padding: 0 10px; margin: 10px 0; font-size: 0.9rem; }
.clip-label { font-weight: 500; color: #666; letter-spacing: 1px; }
.clip-value { color: #333; letter-spacing: 1px; }
.action-buttons { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; }
.mark-btn { flex: 1; min-width: 120px; }
.mark-btn:disabled { background-color: #6c757d !important; border-color: #6c757d !important; cursor: not-allowed; opacity: 0.6; }
.calculate-btn { margin-top: 10px; display: flex; justify-content: center; align-items: center; gap: 5px; }
.error-message { color: #dc3545; font-size: 0.875rem; margin-top: 5px; }
.form-help { color: #6c757d; font-size: 0.875rem; margin-top: 5px; }
.image-counter { color: #333; margin-right: 0.5rem; }
.edit-info { text-align: right; }
.fade-enter-active, .fade-leave-active { transition: opacity 0.2s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
.button-row { display: flex; gap: 10px; margin-top: 10px; }
.button-row .calculate-btn { flex: 1; margin-top: 0; }
.button-row .mark-btn { flex: 1; min-width: auto; }
.modal-content { border-radius: 30px; }
.modal-header, .modal-body { padding: 1rem 2rem; }
.modal-header .btn-close { color: #000; opacity: 1; }
.modal-body th, .modal-body td { color: #8D8D8D; font-size: 13px; font-style: normal; font-weight: 600; line-height: normal; letter-spacing: 1.95px; border: none; white-space: nowrap; }
.action-buttons-container { position: relative; }
.action-toggle-btn { display: flex; align-items: center; gap: 8px; font-size: 20px; padding: 4px 20px; border-radius: 20px; transition: all 0.2s ease; }
.action-toggle-btn.active { background-color: #333; color: #fff; }
.collapsible-action-buttons { position: absolute; top: 40px; left: 0; z-index: 10; padding: 5px; background: #fff; border: 1px solid #dbdbdb; border-radius: 18px; min-width: 180px; }
.collapsible-action-buttons .btn { width: 100%; margin-bottom: 8px; font-size: 0.85rem; }
.collapsible-action-buttons .btn:last-child { margin-bottom: 0; }
.text-truncate { overflow: hidden; white-space: nowrap; text-overflow: ellipsis; }
.placeholder-image-container { display: flex; align-items: center; justify-content: center; }
.placeholder-image { width: 100%; }
.pagination-info { font-weight: 600; color: #333; min-width: 80px; text-align: center; letter-spacing: 1px; }
.modal-body-filter { padding: 1rem 2rem; border-bottom: 1px solid #e0e0e0; background-color: #f8f9fa; }
.filter-row { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 1rem; align-items: end; }
.filter-field { display: flex; flex-direction: column; }
.filter-label { font-size: 0.85rem; font-weight: 500; color: #333; margin-bottom: 0.25rem; }
.filter-input, .filter-select { height: 38px; font-size: 0.9rem; border-radius: 8px; }
.filter-actions { display: flex; align-items: flex-end; }
.filter-stats { margin-top: 0.5rem; text-align: right; }
.modal-footer-pagination { padding: 1rem 2rem; border-top: 1px solid #e0e0e0; }
.pagination-controls { display: flex; align-items: center; justify-content: center; gap: 0.5rem; }
.pagination-btn { min-width: 40px; padding: 0.375rem 0.75rem; }
.pagination-jump { display: flex; align-items: center; gap: 0.5rem; }
.pagination-label { font-size: 0.9rem; color: #666; white-space: nowrap; }
.pagination-input { width: 60px; height: 38px; text-align: center; font-size: 0.9rem; border-radius: 8px; }
.pagination-input::-webkit-outer-spin-button, .pagination-input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
.pagination-input[type=number] { -moz-appearance: textfield; }
.pagination-total { font-size: 0.9rem; color: #666; white-space: nowrap; }
.pagination-current { text-align: center; margin-top: 0.5rem; font-weight: 600; color: #333; }
@media (max-width: 991px) { .edit-info { margin-top: 0.5rem; text-align: left; } }
@media (max-width: 768px) {
  .action-buttons { flex-direction: column; }
  .action-buttons .btn { width: 100%; }
  .form-section { padding: 15px; }
  .image-preview-container { min-height: 300px; }
  .button-row { flex-direction: column; gap: 8px; }
  .modal-body-filter { padding: 1rem; }
  .filter-row { grid-template-columns: 1fr; gap: 0.75rem; }
  .filter-actions { justify-content: stretch; }
  .filter-actions .btn { width: 100%; }
  .pagination-controls { flex-wrap: wrap; gap: 0.25rem; }
  .pagination-input { width: 50px; height: 32px; }
  .pagination-btn { min-width: 36px; padding: 0.25rem 0.5rem; }
}
</style>